/**
 * PoolTagPicker - OCP-428.
 *
 * Wizard-first (OCP-416) placement-pool selector shared by every
 * "add a server" surface (Coolify register form + the Add-VPS modal's
 * provision and register tabs). Before this component the three surfaces
 * disagreed: one used a free-text input, the others a hardcoded <select>
 * that omitted the demo pool, so the operator could not register a server
 * into `tenant.demo_pool_tag` (OCP-403) from the UI.
 *
 * The known pools come from `knownPoolOptions()` (registry/setting-driven,
 * demo reads the live `tenant.demo_pool_tag`), and a "custom / advanced"
 * escape hatch keeps arbitrary tags (e.g. `dedicated-acme`) typeable, so
 * nothing regresses versus the old free-text input.
 *
 * Placement (pool_tag) vs monitoring (server_role) stay separate: the
 * OCP-365 role preset/lock still lives in the parent forms, keyed on the
 * pool tag value this picker feeds back via `onChange`.
 */
import { useMemo } from 'react';

import { knownPoolOptions } from '../lib/pool-role';

/** Sentinel select value that switches the picker into free-text mode. */
const CUSTOM_SENTINEL = '__custom__';

const SELECT_CLASS =
  'mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm';
const INPUT_CLASS =
  'mt-2 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm';
const HINT_CLASS = 'mt-1 block text-xs text-gray-500';

interface PoolTagPickerProps {
  value: string;
  onChange: (value: string) => void;
  /** Live `tenant.demo_pool_tag`, so the demo option tracks the setting. */
  demoPoolTag: string;
  /** data-testid for the <select> (e.g. 'reg-pool-tag'). */
  selectTestId?: string;
  /** data-testid for the free-text escape-hatch <input>. */
  customTestId?: string;
}

export function PoolTagPicker({
  value,
  onChange,
  demoPoolTag,
  selectTestId,
  customTestId,
}: PoolTagPickerProps) {
  const options = useMemo(() => knownPoolOptions(demoPoolTag), [demoPoolTag]);
  const active = options.find((o) => o.value === value);
  // Custom mode when the value isn't one of the known pools (covers
  // dedicated-* and any legacy tag typed by hand).
  const isCustom = !active;
  const selectValue = isCustom ? CUSTOM_SENTINEL : value;

  return (
    <div>
      <select
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === CUSTOM_SENTINEL) {
            // Entering custom mode: clear a previously-known value so the
            // operator types a fresh tag; keep an already-custom value.
            onChange(isCustom ? value : '');
          } else {
            onChange(v);
          }
        }}
        data-testid={selectTestId}
        className={SELECT_CLASS}
      >
        {options.map((o, i) => (
          <option key={`${o.value}-${i}`} value={o.value}>
            {o.label} - {o.description}
          </option>
        ))}
        <option value={CUSTOM_SENTINEL}>Custom / advanced pool…</option>
      </select>
      {isCustom ? (
        <>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="e.g. dedicated-acme"
            data-testid={customTestId}
            className={INPUT_CLASS}
          />
          <span className={HINT_CLASS}>
            Advanced: any pool tag. Use <code>dedicated-&lt;customer&gt;</code> for a
            single-tenant compliance pool.
          </span>
        </>
      ) : (
        <span className={HINT_CLASS}>{active?.description}</span>
      )}
    </div>
  );
}
